import { useReceptionLocale as useOperationsLocale } from '../../../reception/locale';
import { useReceptionLocale } from '../../../reception/locale';
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Bell,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ListChecks,
  Loader2,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { queryPresets } from '../../../config/queryConfig';
import { receptionKeys } from '../../../queries/reception/receptionKeys';
import { getQueryLoadPhase } from '../../../queries/core/loadState';
import {
  acknowledgeReceptionDailySummaryResponse,
  fetchReceptionDailySummaryResponse,
} from '../../../api/receptionWorkflows';
import { ReceptionDialog } from '../../../components/reception/ReceptionDialog';

type Tone = 'orange' | 'amber' | 'violet' | 'sky' | 'emerald' | 'red' | 'slate';
type SummaryTab = 'overview' | 'sessions' | 'tasks';

interface Session {
  id: number;
  group_id: number;
  group_code?: string | null;
  course_name?: string | null;
  group_name?: string | null;
  session_date: string | null;
  status: string;
  session_title: string;
  session_description: string | null;
}

interface TaskItem {
  id: number;
  title: string;
  priority: 'high' | 'medium' | 'low' | string;
  status: string;
  expires_at: string | null;
  completed_count: number;
  target_count: number;
  progress_percentage: number;
  task_type: string;
}

interface DailySummary {
  pending_students: number;
  join_requests: number;
  course_interests: number;
  todays_sessions: Session[];
  todays_sessions_count: number;
  undone_tasks?: TaskItem[];
  undone_tasks_count?: number;
  already_seen: boolean;
  acknowledged: boolean;
  notification_date: string;
}

interface DailyNotificationModalProps {
  onClose: () => void;
}

interface SummaryMetricItem {
  label: string;
  value: number;
  hint: string;
  icon: LucideIcon;
  tone: Tone;
  path: string;
  translate?: boolean;
}

const tones: Record<Tone, { soft: string; card: string; badge: string; bar: string }> = {
  orange: {
    soft: 'bg-orange-50 text-orange-700',
    card: 'bg-orange-50 text-orange-700 border-orange-200',
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    bar: 'bg-orange-500',
  },
  amber: {
    soft: 'bg-amber-50 text-amber-700',
    card: 'bg-amber-50 text-amber-700 border-amber-200',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    bar: 'bg-amber-500',
  },
  violet: {
    soft: 'bg-violet-50 text-violet-700',
    card: 'bg-violet-50 text-violet-700 border-violet-200',
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    bar: 'bg-violet-500',
  },
  sky: {
    soft: 'bg-sky-50 text-sky-700',
    card: 'bg-sky-50 text-sky-700 border-sky-200',
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    bar: 'bg-sky-500',
  },
  emerald: {
    soft: 'bg-emerald-50 text-emerald-700',
    card: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    bar: 'bg-emerald-500',
  },
  red: {
    soft: 'bg-red-50 text-red-700',
    card: 'bg-red-50 text-red-700 border-red-200',
    badge: 'bg-red-50 text-red-700 border-red-200',
    bar: 'bg-red-500',
  },
  slate: {
    soft: 'bg-slate-50 text-slate-700',
    card: 'bg-white text-slate-700 border-slate-200',
    badge: 'bg-white text-slate-700 border-slate-200',
    bar: 'bg-slate-500',
  },
};

function clampPercent(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.min(Math.max(Math.round(value), 0), 100);
}

const tabLabels: Record<SummaryTab, string> = {
  overview: 'Overview',
  sessions: 'Sessions',
  tasks: 'Tasks',
};

function formatDateLabel(value: string | null | undefined, language: 'ar' | 'en') {
  if (!value) return '';
  const [year, month, day] = value.split('-').map((part) => Number(part));
  const date = year && month && day ? new Date(year, month - 1, day) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const locale = language === 'ar' ? 'ar-EG' : 'en-US';
  const weekday = date.toLocaleDateString(locale, { weekday: 'long' });
  const shortDate = date.toLocaleDateString(locale, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
  return `${weekday} · ${shortDate}`;
}

function formatTime(value: string | null | undefined, language: 'ar' | 'en') {
  if (!value) return language === 'ar' ? 'يُحدد لاحقًا' : 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return language === 'ar' ? 'يُحدد لاحقًا' : 'TBD';
  return date.toLocaleTimeString(language === 'ar' ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit' });
}

function titleCase(value?: string | null) {
  const cleaned = String(value || '').replace(/_/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
}

function getSessionName(session: Session) {
  return session.course_name || session.session_title || session.group_name || `Session #${session.id}`;
}

function getSessionGroupLabel(session: Session) {
  if (session.group_code) return `Group ${session.group_code}`;
  if (session.group_name) return session.group_name;
  return `Group #${session.group_id}`;
}

function getPriorityTone(priority?: string | null): Tone {
  const normalized = String(priority || '').toLowerCase();
  if (normalized.includes('high') || normalized.includes('urgent')) return 'red';
  if (normalized.includes('medium')) return 'amber';
  if (normalized.includes('low')) return 'emerald';
  return 'sky';
}

function Badge({ children, tone = 'slate' }: { children: React.ReactNode; tone?: Tone }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-black ${tones[tone].badge}`}>
      {children}
    </span>
  );
}

function EmptyPanel({ icon: Icon, title, text }: { icon: LucideIcon; title: string; text: string }) {
  const { t: receptionText } = useReceptionLocale();

  return (
    <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-6 text-center shadow-sm">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-slate-50 text-slate-400">
        <Icon size={22} />
      </div>
      <p className="mt-3 text-sm font-black text-slate-900">{receptionText(title)}</p>
      <p className="mt-1 break-words text-sm font-semibold text-slate-500">{receptionText(text)}</p>
    </div>
  );
}

function SummaryMetric({
  item,
  onOpen,
}: {
  item: SummaryMetricItem;
  onOpen: (path: string) => void;
}) {
  const { t: receptionText } = useReceptionLocale();

  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(item.path)}
      className={`group min-w-0 !rounded-3xl border p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${tones[item.tone].card}`}
    >
      <div className="flex items-center gap-3">
        <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${tones[item.tone].soft}`}>
          <Icon size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-2xl font-black text-slate-950">{item.value}</p>
          <p className="break-words text-sm font-bold leading-5 text-slate-800">{receptionText(item.label)}</p>
          <p className="mt-0.5 break-words text-xs font-semibold leading-5 text-slate-500">{receptionText(item.hint)}</p>
        </div>
        <ChevronRight size={17} className="text-slate-300 group-hover:text-violet-600" />
      </div>
    </button>
  );
}

function SessionRow({ session }: { session: Session }) {
  const { t: receptionText, language } = useReceptionLocale();

  const status = titleCase(session.status) || 'Scheduled';
  const statusTone: Tone = status.toLowerCase().includes('complete')
    ? 'emerald'
    : status.toLowerCase().includes('cancel')
      ? 'red'
      : 'violet';

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="rounded-xl bg-violet-50 px-3 py-2 text-sm font-black text-violet-700">
            {formatTime(session.session_date, language)}
          </span>
          <div className="min-w-0">
            <p className="break-words text-sm font-bold text-slate-950">{getSessionName(session)}</p>
            <p className="break-words text-xs font-semibold leading-5 text-slate-500">
              {receptionText(getSessionGroupLabel(session))}
              {session.session_description ? ` · ${session.session_description}` : ''}
            </p>
          </div>
        </div>
        <Badge tone={statusTone}>{receptionText(status)}</Badge>
      </div>
    </article>
  );
}

function TaskRow({ task }: { task: TaskItem }) {
  const { t: receptionText } = useReceptionLocale();

  const percent = clampPercent(
    task.progress_percentage ?? (task.target_count ? (task.completed_count / task.target_count) * 100 : 0),
  );
  const priorityTone = getPriorityTone(task.priority);

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words text-sm font-bold text-slate-950">{task.title}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge tone={priorityTone}>{receptionText(String(task.priority || 'medium').toLowerCase())}</Badge>
            <Badge tone="violet">{receptionText(titleCase(task.task_type) || "Task")}</Badge>
          </div>
        </div>
        <span className="shrink-0 text-xs font-black text-slate-500">
          {task.completed_count}/{task.target_count}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${tones[priorityTone].bar}`} style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-2 text-xs font-semibold text-slate-500">{percent}{receptionText("% complete")}</p>
    </article>
  );
}

function ModalShell({
  children,
  dateLabel,
  footer,
  onClose,
  closeDisabled = false,
}: {
  children: React.ReactNode;
  dateLabel?: string;
  footer?: React.ReactNode;
  onClose?: () => void;
  closeDisabled?: boolean;
}) {
  const { t: receptionText } = useReceptionLocale();

  return (
    <ReceptionDialog
      open
      title={receptionText("Daily Summary")}
      onClose={onClose ?? (() => undefined)}
      closeLabel={receptionText("Close daily summary")}
      closeOnEscape={Boolean(onClose) && !closeDisabled}
      closeOnBackdrop={Boolean(onClose) && !closeDisabled}
      showClose={Boolean(onClose)}
      closeDisabled={closeDisabled}
      rootClassName="reception-daily-summary"
      panelClassName="reception-dialog-panel--large"
      bodyClassName="reception-dialog-body--flush"
      footer={footer}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-100 bg-violet-50 px-5 py-3 text-sm font-semibold text-violet-800">
          <CalendarDays size={17} aria-hidden="true" />
          <span className="break-words">{dateLabel || receptionText("Reception overview")}</span>
        </div>
        {children}
      </div>
    </ReceptionDialog>
  );
}

export default function DailyNotificationModal({ onClose }: DailyNotificationModalProps) {
  const { t: operationsText } = useOperationsLocale();

  const { t: receptionText, language } = useReceptionLocale();
  const queryClient = useQueryClient();
  const acknowledgingRef = React.useRef(false);

  const navigate = useNavigate();
  const [tab, setTab] = React.useState<SummaryTab>('overview');
  const [error, setError] = React.useState<string | null>(null);

  const summaryQuery = useQuery({
    queryKey: receptionKeys.dailySummary(),
    queryFn: async (): Promise<DailySummary> => {
      const response = await fetchReceptionDailySummaryResponse();
      if (!response.ok) throw new Error('Failed to load daily summary');
      return response.json();
    },
    ...queryPresets.dynamicList,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: async () => {
      const response = await acknowledgeReceptionDailySummaryResponse();
      if (!response.ok) throw new Error('Failed to acknowledge notification');
    },
  });

  const summary = summaryQuery.data ?? null;
  const loadPhase = getQueryLoadPhase({
    isFetching: summaryQuery.isFetching,
    isError: summaryQuery.isError,
    hasData: summaryQuery.data !== undefined,
  });
  const loading = loadPhase === 'initial';
  const queryError = summaryQuery.error instanceof Error ? summaryQuery.error.message : null;
  const mergedError = error || queryError;

  const handleAcknowledge = React.useCallback(
    async (afterClose?: () => void) => {
      if (acknowledgingRef.current) return;
      acknowledgingRef.current = true;
      const notificationDate = summary?.notification_date;
      setError(null);
      try {
        await acknowledgeMutation.mutateAsync();
        // Cancel older reads before marking only this day's notification as seen.
        await queryClient.cancelQueries({ queryKey: receptionKeys.dailySummary(), exact: true });
        queryClient.setQueryData<DailySummary>(receptionKeys.dailySummary(), current =>
          current && current.notification_date === notificationDate
            ? { ...current, already_seen: true, acknowledged: true }
            : current);
        afterClose?.();
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : operationsText("Error acknowledging notification"));
      } finally {
        acknowledgingRef.current = false;
      }
    },
    [acknowledgeMutation, onClose, queryClient, summary?.notification_date, operationsText],
  );

  const handleOpenPath = React.useCallback(
    (path: string) => {
      void handleAcknowledge(() => navigate(path));
    },
    [handleAcknowledge, navigate],
  );

  if (loading) {
    return (
      <ModalShell dateLabel={receptionText("Loading live data")}>
        <div className="grid min-h-[260px] place-items-center bg-slate-50/70 p-8">
          <div className="text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-violet-50 text-violet-700">
              <Loader2 className="animate-spin" size={26} />
            </div>
            <p className="mt-4 text-sm font-black text-slate-900">{receptionText("Loading daily summary...")}</p>

          </div>
        </div>
      </ModalShell>
    );
  }

  if (mergedError || !summary) {
    return (
      <ModalShell dateLabel={receptionText("Could not load data")} onClose={onClose}>
        <div className="grid min-h-[260px] place-items-center bg-slate-50/70 p-8">
          <div className="max-w-sm text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-700">
              <AlertCircle size={26} />
            </div>
            <p className="mt-4 text-sm font-black text-slate-900">{receptionText("Failed to load daily summary")}</p>
            <p className="mt-1 text-sm font-semibold text-slate-500">{mergedError || receptionText("Failed to load summary")}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 h-11 !rounded-2xl border border-slate-200 bg-white px-6 text-sm font-black text-slate-700 shadow-sm hover:bg-slate-50"
            >{receptionText("Close")}</button>
          </div>
        </div>
      </ModalShell>
    );
  }

  const tasks = summary.undone_tasks || [];
  const tasksCount = summary.undone_tasks_count ?? tasks.length;
  const sessions = summary.todays_sessions || [];
  const sessionsCount = summary.todays_sessions_count ?? sessions.length;
  const pendingItems: SummaryMetricItem[] = [
    {
      label: 'Registered Students',
      value: summary.pending_students,
      hint: 'No payment submitted',
      icon: Users,
      tone: 'orange',
      path: '/reception/students?category=pending',
    },
    {
      label: 'Join Requests',
      value: summary.join_requests,
      hint: 'Approve or reject',
      icon: UserPlus,
      tone: 'sky',
      path: '/reception/requests',
    },
    {
      label: 'Course Interests',
      value: summary.course_interests,
      hint: 'Need follow-up',
      icon: Bell,
      tone: 'violet',
      path: '/reception/course-interests',
    },
  ];
  const hasActionableWork = pendingItems.some((item) => item.value > 0) || sessionsCount > 0 || tasksCount > 0;
  const visibleSessions = sessions.slice(0, 4);
  const visibleTasks = tasks.slice(0, 3);

  return (
    <ModalShell
      dateLabel={formatDateLabel(summary.notification_date, language)}
      onClose={() => {
        void handleAcknowledge();
      }}
      closeDisabled={acknowledgeMutation.isPending}
      footer={
        <div className="flex w-full justify-end">
          <button
            type="button"
            onClick={() => {
              void handleAcknowledge();
            }}
            disabled={acknowledgeMutation.isPending}
            className="min-h-11 !rounded-xl bg-violet-700 px-8 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            {acknowledgeMutation.isPending ? receptionText("Acknowledging...") : receptionText("✓ Got it")}
          </button>
        </div>
      }
    >
      <div className="shrink-0 border-b border-slate-100 bg-white px-4 pt-3 sm:px-5">
        <div className="flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {(['overview', 'sessions', 'tasks'] as SummaryTab[]).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setTab(item)}
              className={`shrink-0 !rounded-t-2xl px-4 py-2 text-sm font-black capitalize ${
                tab === item ? 'bg-violet-50 text-violet-700' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {receptionText(tabLabels[item])}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/70 p-4 [scrollbar-width:thin] sm:p-5">
        {mergedError ? (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {mergedError}
          </div>
        ) : null}

        {tab === 'overview' ? (
          <div className="space-y-5">
            <section>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-black uppercase tracking-[0.12em] text-slate-500">{receptionText("Pending Work Items")}</h3>
                <Badge tone={hasActionableWork ? 'amber' : 'emerald'}>
                  {hasActionableWork ? receptionText("Needs action") : receptionText("Clear")}
                </Badge>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {pendingItems.map((item) => (
                  <SummaryMetric key={item.label} item={item} onOpen={handleOpenPath} />
                ))}
              </div>
            </section>

            <section className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-4">
                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <CalendarDays size={24} />
                </div>
                <div className="min-w-0">
                  <p className="text-3xl font-black text-slate-950">{sessionsCount}</p>
                  <p className="font-black text-slate-800">{receptionText("Sessions Scheduled")}</p>

                </div>
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-black uppercase tracking-[0.12em] text-slate-500">{receptionText("Next Sessions")}</h3>
                <button
                  type="button"
                  onClick={() => setTab('sessions')}
                  className="text-sm font-black text-violet-700"
                >{receptionText("View all")}</button>
              </div>
              {visibleSessions.length > 0 ? (
                <div className="space-y-2">
                  {visibleSessions.slice(0, 3).map((session) => (
                    <SessionRow key={session.id} session={session} />
                  ))}
                </div>
              ) : (
                <EmptyPanel
                  icon={CheckCircle2}
                  title={receptionText("No sessions scheduled")}
                  text={receptionText("There are no live sessions in today's reception summary.")}
                />
              )}
            </section>
          </div>
        ) : null}

        {tab === 'sessions' ? (
          <div className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-4">
              <h3 className="text-lg font-black text-slate-950">{receptionText("Today's Sessions")}</h3>
              <p className="mt-1 text-sm font-semibold text-slate-500">{receptionText("Review today's scheduled sessions before attendance starts.")}</p>
            </div>
            {sessions.length > 0 ? (
              <div className="space-y-2">
                {sessions.map((session) => (
                  <SessionRow key={session.id} session={session} />
                ))}
              </div>
            ) : (
              <EmptyPanel icon={CalendarDays} title={receptionText("No sessions today")} text={receptionText("The live summary has no sessions for today.")} />
            )}
          </div>
        ) : null}

        {tab === 'tasks' ? (
          <div className="space-y-4">
            <div className="rounded-3xl border border-sky-200 bg-sky-50 p-4">
              <div className="flex items-center gap-4">
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-sky-100 text-sky-700">
                  <ListChecks size={24} />
                </div>
                <div>
                  <p className="text-3xl font-black text-slate-950">{tasksCount}</p>
                  <p className="font-black text-slate-800">{receptionText("Undone Tasks")}</p>
                  <p className="text-sm font-semibold text-slate-500">{receptionText("Showing first ")}{visibleTasks.length}{receptionText("; open tasks page for the full list.")}</p>
                </div>
              </div>
            </div>
            {visibleTasks.length > 0 ? (
              <div className="space-y-2">
                {visibleTasks.map((task) => (
                  <TaskRow key={task.id} task={task} />
                ))}
              </div>
            ) : (
              <EmptyPanel icon={ListChecks} title={receptionText("No open tasks")} text={receptionText("There are no undone assigned tasks in the live summary.")} />
            )}
            <button
              type="button"
              onClick={() => handleOpenPath('/reception/tasks')}
              className="w-full !rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-black text-violet-700 hover:bg-violet-100"
            >{receptionText("Open My Tasks")}</button>
          </div>
        ) : null}
      </div>
    </ModalShell>
  );
}
